import { useEffect, useMemo, useState } from "react";
import { useVersionModulesList } from "../../../../../query/hooks/QueryHooksLinkedProgrammes";
import { useNetsuiteItems } from "../../../../../query/hooks/QueryHooksVersionDetails";
import useVersionModuleBulkActions from "./useVersionModuleBulkActions";

const emptyFilterState = { arr: [], checkObj: {} };

/**
 * Drives the Version Modules table (SPEC3): client-side search/status/term/year
 * filtering, sorting and pagination over the unpaginated module list. Row
 * selection and the bulk "Unlink Modules" flow live in `useVersionModuleBulkActions`.
 * @param {string | number} programmeId PMC programme id.
 * @param {string | number} versionId Version id to scope modules to.
 * @param {boolean} [isLocked] Whether the version's settings are locked - blocks unlinking.
 */
function useVersionModules({ programmeId, versionId, isLocked = false }) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState(emptyFilterState);
    const [termFilter, setTermFilter] = useState(emptyFilterState);
    const [yearFilter, setYearFilter] = useState(emptyFilterState);

    const [sortKey, setSortKey] = useState("Portal");
    const [sortOrder, setSortOrder] = useState("ASC");

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    /* Date.now() (not a boolean toggle) - a boolean can only ever be true/false, so two refreshes landing on the same value in a row would both hit the same cache entry and silently skip the refetch. */
    const [refreshKey, setRefreshKey] = useState(0);
    const refresh = () => setRefreshKey(Date.now());

    const {
        data: rows,
        isLoading: loading,
        isError,
    } = useVersionModulesList(programmeId, versionId, refreshKey);

    const { items: netsuiteList, isLoading: isNetsuiteLoading } = useNetsuiteItems();

    /*
     * Prerequisite ids resolve to names against this same version's full (unfiltered,
     * unpaged) module list, matching the pre-redesign AssignedTable.js's approach.
     */
    const moduleNameById = useMemo(() => {
        const map = new Map();
        rows.forEach((row) => {
            map.set(row.Id, row.Portal);
        });
        return map;
    }, [rows]);

    const statusOptions = useMemo(
        () => Array.from(new Set(rows.map((row) => row.module_status).filter(Boolean)))
            .map((value) => ({ value, label: value })),
        [rows]
    );

    const termOptions = useMemo(
        () => Array.from(new Set(
            rows.map((row) => row.term).filter((value) => value !== null && value !== undefined && value !== "")
        )).map((value) => ({ value: String(value), label: String(value) })),
        [rows]
    );

    const yearOptions = useMemo(
        () => Array.from(new Set(
            rows.map((row) => row.year).filter((value) => value !== null && value !== undefined && value !== "")
        )).map((value) => ({ value: String(value), label: String(value) })),
        [rows]
    );

    const filteredRows = useMemo(() => {
        const searchTerm = search.trim().toLowerCase();

        return rows.filter((row) => {
            if (searchTerm) {
                const matchesSearch =
                    String(row.Portal || "").toLowerCase().includes(searchTerm) ||
                    String(row.Code || "").toLowerCase().includes(searchTerm);

                if (!matchesSearch) {
                    return false;
                }
            }

            if (statusFilter.arr.length && !statusFilter.arr.includes(row.module_status)) {
                return false;
            }

            if (termFilter.arr.length && !termFilter.arr.includes(String(row.term))) {
                return false;
            }

            if (yearFilter.arr.length && !yearFilter.arr.includes(String(row.year))) {
                return false;
            }

            return true;
        });
    }, [rows, search, statusFilter, termFilter, yearFilter]);

    const sortedRows = useMemo(() => {
        const sorted = [...filteredRows].sort((a, b) => {
            const aValue = a[sortKey];
            const bValue = b[sortKey];

            if (aValue === bValue) {
                return 0;
            }

            if (aValue === null || aValue === undefined) {
                return -1;
            }

            if (bValue === null || bValue === undefined) {
                return 1;
            }

            return aValue > bValue ? 1 : -1;
        });

        return sortOrder === "ASC" ? sorted : [...sorted].reverse();
    }, [filteredRows, sortKey, sortOrder]);

    const totalRows = sortedRows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / perPage));
    const pagedRows = sortedRows.slice((page - 1) * perPage, page * perPage);

    const rangeStart = totalRows === 0 ? 0 : (page - 1) * perPage + 1;
    const rangeEnd = Math.min(page * perPage, totalRows);

    const allMatchingIds = useMemo(() => sortedRows.map((row) => row.Id), [sortedRows]);

    const filterSignature = `${search}|${statusFilter.arr.join(",")}|${termFilter.arr.join(",")}|${yearFilter.arr.join(",")}`;

    useEffect(() => {
        setPage(1);
    }, [filterSignature]);

    /* Keeps the current page valid after a bulk unlink shrinks the row count, or perPage changes. */
    useEffect(() => {
        setPage((current) => Math.min(current, totalPages));
    }, [totalPages]);

    const resetFilters = () => {
        setSearch("");
        setStatusFilter(emptyFilterState);
        setTermFilter(emptyFilterState);
        setYearFilter(emptyFilterState);
        setPage(1);
    };

    const changeSort = (field) => {
        setPage(1);
        setSortKey(field);
        setSortOrder((current) => (sortKey === field && current === "ASC" ? "DESC" : "ASC"));
    };

    const bulkActions = useVersionModuleBulkActions({
        programmeId,
        versionId,
        isLocked,
        allMatchingIds,
        filterSignature,
        onUnlinked: refresh,
    });

    return {
        search,
        setSearch,
        statusFilter,
        setStatusFilter,
        statusOptions,
        termFilter,
        setTermFilter,
        termOptions,
        yearFilter,
        setYearFilter,
        yearOptions,
        resetFilters,
        loading,
        isError,
        rows: pagedRows,
        allFilteredRows: sortedRows,
        netsuiteList,
        isNetsuiteLoading,
        moduleNameById,
        totalRows,
        totalPages,
        page,
        setPage,
        perPage,
        setPerPage,
        rangeStart,
        rangeEnd,
        sortKey,
        sortOrder,
        changeSort,
        isLocked,
        ...bulkActions,
        refresh,
    };
}

export default useVersionModules;
